// observations.js — the page, reading itself.
//
// Every entry here is a RULE over the payload, never a sentence about today.
// That distinction is the whole point: a hardcoded finding is true the morning
// it is written and quietly false a week later, on a page whose purpose is to
// be trusted in a room. A rule either fires on tomorrow's data or it does not,
// and when it fires it recomputes its own figures.
//
// FOUR THINGS EVERY OBSERVATION MUST CARRY, because an observation without them
// is an assertion:
//   · strength   — what it was computed over. Two cabs is not a trend.
//   · body       — the arithmetic, in words, so it can be checked not believed.
//   · notThis    — the wrong conclusion the reader is most likely to draw.
//                  This page's figures are presence revenue on the supply
//                  plane; nearly every misreading is a reader importing a
//                  meaning from the settled ledger or from gross billings.
//   · severity   — act / watch / good / structural. Structural means it is a
//                  property of the instrument, not of the fleet.
//
// A rule returns null when it does not fire. Nothing here is written to always
// have something to say.

const inr = (v) => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`;
const n = (v) => Number(v).toLocaleString('en-IN');
const pctOf = (a, b) => (b ? Math.round((a / b) * 100) : null);
const plural = (v, one, many) => `${n(v)} ${Number(v) === 1 ? one : many}`;
// Registry spellings are lowercase; a page that says "pune" in a sentence about
// money reads as a dump rather than a document.
const place = (c) => (c ? c.charAt(0).toUpperCase() + c.slice(1) : 'an unrecorded city');

// ── helpers over the payload ────────────────────────────────────────────────
const monthsOf = (data) => data.months.filter((m) => m.summary);
const cabsOf = (data) => data.tape?.cabs || [];
const median = (xs) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const i = Math.floor(s.length / 2);
  return s.length % 2 ? s[i] : (s[i - 1] + s[i]) / 2;
};

const RULES = [
  // ── DECLINE ───────────────────────────────────────────────────────────────
  {
    id: 'perDayTrend',
    lens: 'decline',
    run: (data) => {
      const ms = monthsOf(data);
      if (ms.length < 2) return null;
      const cur = ms[ms.length - 1];
      const s = cur.summary;
      // A part month against a whole one is not a comparison; the per-day
      // delta is the only honest read and the rule refuses the other one.
      const delta = s.partial ? s.momPerDayPct : s.momPct;
      if (delta == null) return null;
      const basis = s.partial ? 'per serving day' : 'month on month';
      const down = delta < 0;
      return {
        severity: down ? (delta <= -20 ? 'act' : 'watch') : 'good',
        headline: down
          ? `${cur.label} is earning ${Math.abs(delta)}% less ${basis}`
          : `${cur.label} is earning ${delta}% more ${basis}`,
        body: `${inr(s.rupees)} across ${plural(s.daysServed, 'serving day', 'serving days')}, `
            + `at ${inr(s.perScreenDay)} per screen-day against ${inr(ms[ms.length - 2].summary.perScreenDay)} `
            + `the month before.`,
        strength: `${ms.length} months of record; ${plural(s.daysServed, 'day', 'days')} in the current one`,
        notThis: s.partial
          ? `Not a ${Math.abs(s.momPct)}% collapse — that raw figure is mostly `
            + `${s.daysServed} days measured against a full month.`
          : 'Not a change in demand: this is presence-verified supply revenue, which moves with '
            + 'how much of the fleet ran as much as with what was sold.',
      };
    },
  },
  {
    id: 'cabsFalling',
    lens: 'decline',
    run: (data) => {
      const keys = data.tape?.monthKeys || [];
      if (keys.length < 2) return null;
      const [prev, cur] = keys.slice(-2);
      const fell = cabsOf(data)
        .map((c) => ({ c, a: c.months?.[prev] || 0, b: c.months?.[cur] || 0 }))
        .filter((x) => x.a > 0 && x.b < x.a * 0.5)
        .sort((x, y) => (y.a - y.b) - (x.a - x.b));
      if (fell.length < 2) return null;
      const lost = fell.reduce((s, x) => s + (x.a - x.b), 0);
      const dead = fell.filter((x) => x.b === 0);
      return {
        severity: 'act',
        headline: `${plural(fell.length, 'cab', 'cabs')} more than halved between the last two months`,
        body: `${inr(lost)} of monthly run-rate came off those cabs alone`
            + (dead.length
              ? `, and ${dead.length} of them earned nothing at all this month `
                + `(${dead.slice(0, 4).map((x) => x.c.plate).join(', ')}${dead.length > 4 ? ', …' : ''}).`
              : '.')
            + ` Worst single fall: ${fell[0].c.plate} in ${place(fell[0].c.city)}, `
            + `${inr(fell[0].a)} → ${inr(fell[0].b)}.`,
        strength: `${plural(cabsOf(data).length, 'cab', 'cabs')} compared across two whole months`,
        notThis: 'Not necessarily broken screens. A cab that stops driving earns nothing for the '
               + 'same reason a cab with a dead screen does, and this page cannot tell them apart — '
               + 'the silence column is the symptom, not the cause.',
      };
    },
  },

  // ── CONCENTRATION AND OUTLIERS ────────────────────────────────────────────
  {
    id: 'concentration',
    lens: 'outlier',
    run: (data, view) => {
      const key = view.month.key;
      const earners = cabsOf(data).map((c) => c.months?.[key] || 0).filter((v) => v > 0);
      if (earners.length < 4) return null;
      const total = earners.reduce((a, b) => a + b, 0);
      const sorted = [...earners].sort((a, b) => b - a);
      const topShare = pctOf(sorted[0], total);
      const halfIdx = (() => {
        let run = 0;
        for (let i = 0; i < sorted.length; i += 1) { run += sorted[i]; if (run >= total / 2) return i + 1; }
        return sorted.length;
      })();
      if (halfIdx > earners.length / 2) return null;
      const med = median(earners);
      return {
        severity: 'watch',
        headline: `Half of ${view.month.label}'s money came from ${plural(halfIdx, 'cab', 'cabs')} of ${earners.length}`,
        body: `Best cab ${inr(sorted[0])} — ${topShare}% of the month on its own — against a median `
            + `earning cab of ${inr(med)}. The top cab is ${(sorted[0] / med).toFixed(1)}× the median one.`,
        strength: `${plural(earners.length, 'cab that earned', 'cabs that earned')} in ${view.month.label}`,
        notThis: 'Not proof those cabs are better placed. Concentration this narrow is usually '
               + 'uptime, not geography — check the silence column for the same plates before '
               + 'concluding anything about where they drive.',
      };
    },
  },
  {
    // REPLACES an earlier rule of mine that compared money per FITTED cab and
    // headlined "a Pune cab earned 3.0x a Mumbai one". Every figure in it was
    // right and the sentence was still misleading: money per fitted cab mixes
    // attendance with yield, and on this fleet the two point OPPOSITE ways —
    // Mumbai's revenue per working cab-day is the highest on the record and
    // flat across all three months, while its attendance collapsed. A reader
    // took the old headline as "Pune performs better". This rule measures
    // attendance alone, which is what the payload can actually support, and
    // says so.
    id: 'cityAttendance',
    lens: 'outlier',
    run: (data, view) => {
      const ms = monthsOf(data);
      if (ms.length < 2) return null;
      const cities = ['mumbai', 'pune'];
      const att = (m, city) => {
        const done = m.days.filter((d) => !d.preService && !d.partial && d.deployed[city] > 0);
        const fit = done.reduce((a, d) => a + d.deployed[city], 0);
        const ran = done.reduce((a, d) => a + d.active[city], 0);
        return fit ? { pct: (ran / fit) * 100, fit, ran } : null;
      };
      const fleet = (m) => {
        const done = m.days.filter((d) => !d.preService && !d.partial && d.deployed.total > 0);
        const fit = done.reduce((a, d) => a + d.deployed.total, 0);
        const ran = done.reduce((a, d) => a + d.active.total, 0);
        return fit ? (ran / fit) * 100 : null;
      };
      const [prev, cur] = ms.slice(-2);
      const rows = cities.map((c) => ({ city: c, a: att(prev, c), b: att(cur, c) }))
        .filter((r) => r.a && r.b && r.b.fit >= 30);
      if (rows.length < 2) return null;
      // HOLDING is within 5pp AND no screen in that city posted a zero month.
      const zeroes = (city) => cabsOf(data)
        .filter((x) => x.city === city && !(x.months?.[cur.key] > 0))
        .map((x) => x.plate);
      const tagged = rows.map((r) => ({
        ...r, delta: r.b.pct - r.a.pct, zeroes: zeroes(r.city),
      }));
      const held = tagged.filter((r) => Math.abs(r.delta) <= 5 && r.zeroes.length === 0);
      const fell = tagged.filter((r) => !held.includes(r)).sort((a, b) => a.delta - b.delta);
      if (!held.length || !fell.length) return null;
      const h = held[0], f = fell[0];
      const fa = fleet(prev), fb = fleet(cur);
      return {
        severity: 'watch',
        headline: `${place(h.city)}'s attendance did not move — the fleet's fall is `
                + `${place(f.city)}'s ${cabsOf(data).filter((c) => c.city === f.city).length} screens`,
        // SCOPE ON EVERY FIGURE. The word "fleet" is reserved for the sum over
        // all cities; a city's own number never wears it. Presenting one
        // city's collapse as the fleet's overstates the damage threefold, and
        // it is the number a sceptical reader checks first.
        body: `${place(h.city)} ran ${h.a.pct.toFixed(1)}% of its fitted cab-days in ${prev.label} and `
            + `${h.b.pct.toFixed(1)}% in ${cur.label}, with no screen posting a zero month. `
            + `${place(f.city)} went ${f.a.pct.toFixed(1)}% → ${f.b.pct.toFixed(1)}%`
            + (f.zeroes.length ? `, ${f.zeroes.length} of its plates at zero (${f.zeroes.join(', ')})` : '')
            + `. Across both cities the fleet went ${fa.toFixed(1)}% → ${fb.toFixed(1)}%.`,
        strength: `${n(rows.reduce((a, r) => a + r.b.fit, 0))} fitted cab-days in ${cur.label}, `
                + `complete days only; the part day is excluded from both months`,
        // The distinction the old rule lost, stated outright.
        notThis: `Not a claim that ${place(h.city)} earns more per working cab-day — it does not, and `
               + `this page cannot show that either way, because it carries no money split by city. `
               + `Attendance is a presence claim, never a revenue one.`,
      };
    },
  },

  // ── THE GATE ──────────────────────────────────────────────────────────────
  {
    id: 'gateRuleChanged',
    lens: 'gate',
    run: (data) => {
      const rules = monthsOf(data)
        .map((m) => ({ label: m.label, rule: m.summary.gateRule }))
        .filter((r) => r.rule);
      const distinct = [...new Set(rules.map((r) => r.rule))];
      if (distinct.length < 2) return null;
      return {
        severity: 'structural',
        headline: 'The gate is not the same rule in every column',
        body: rules.map((r) => `${r.label}: ${r.rule}`).join(' · ') + '. The rule is dated and '
            + 'versioned on the revenue-share agreement, so a month scored under one test cannot '
            + 'be compared like for like with a month scored under another.',
        strength: `${distinct.length} distinct rules across ${rules.length} months`,
        notThis: 'Not a change in fleet behaviour. A pass rate that moved when the rule moved '
               + 'says something about the rule first.',
      };
    },
  },
  {
    id: 'unswept',
    lens: 'gate',
    run: (data, view) => {
      const open = view.unswept;
      if (!open.length) return null;
      const rupees = open.reduce((a, d) => a + d.rupees, 0);
      return {
        severity: 'act',
        headline: `${plural(open.length, 'day', 'days')} carrying ${inr(rupees)} were never scored`,
        body: `The sweep runs at 09:00 IST and scores the previous day. These days are older than `
            + `that window and still have no verdict in device_day_eligibility — earliest `
            + `${open[0].day}, latest ${open[open.length - 1].day}. The money is recorded and `
            + `attributed to neither side of the gate.`,
        strength: `every day in the ${data.days.length}-day record checked against its frozen verdict`,
        notThis: 'Not money the gate withheld. Withheld money has a verdict; this has none, and '
               + 'folding it into the failed bucket by subtraction invents a verdict that does not exist.',
      };
    },
  },
  {
    id: 'unowned',
    lens: 'gate',
    run: (data, view) => {
      const e = view.month.economics;
      const amt = e?.qualified?.unowned?.amount;
      if (!amt) return null;
      return {
        severity: 'act',
        headline: `${inr(amt)} met the gate but has no owner to pay`,
        body: `That money is earned on screens with no row in ownership_history, so the waterfall `
            + `has nobody to hand the premise and device shares to and books the whole of it to house. `
            + `It is ${pctOf(amt, e.qualified.pool)}% of ${view.month.label}'s qualified pool.`,
        strength: `${view.month.label}'s frozen gate verdicts, joined to the ownership registry`,
        notThis: 'Not profit. It is a registry gap presenting as margin, and it reverses the moment '
               + 'the ownership row is written.',
      };
    },
  },
  {
    id: 'gateTrend',
    lens: 'gate',
    run: (data) => {
      const ms = monthsOf(data).filter((m) => m.summary.gateScored > 0);
      if (ms.length < 2) return null;
      const rates = ms.map((m) => ({
        label: m.label,
        pass: pctOf(m.summary.gateQualified, m.summary.gateScored),
        rule: m.summary.gateRule,
      }));
      const first = rates[0], last = rates[rates.length - 1];
      if (Math.abs(last.pass - first.pass) < 8) return null;
      const sameRule = first.rule === last.rule;
      return {
        severity: last.pass < first.pass ? 'watch' : 'good',
        headline: `Cab-days clearing the gate went ${first.pass}% → ${last.pass}%`,
        body: rates.map((r) => `${r.label} ${r.pass}%`).join(' · ')
            + (sameRule ? ' — under the same rule throughout, so this is the fleet moving.'
                        : ' — but the rule changed inside that span, so part of the move is the test, not the fleet.'),
        strength: `${n(ms.reduce((a, m) => a + m.summary.gateScored, 0))} scored cab-days across ${ms.length} months`,
        notThis: 'Not a billing figure. The gate decides what is SHAREABLE with supply; what an '
               + 'advertiser was charged is decided separately, by the presence verdict on the impression.',
      };
    },
  },

  {
    // Verified independently: weekday 356 scored / 81 passed = 22.8%, weekend
    // 127 / 11 = 8.7%, and 13 of the 14 cabs that have ever passed pass more
    // often on a weekday. The per-cab census is the load-bearing evidence
    // because it needs no independence assumption; cab-days are not
    // independent, the same cabs recur.
    id: 'weekendGate',
    lens: 'gate',
    run: (data) => {
      // One era only. The rule is dated on the agreement and July's is a
      // different test, so mixing them would measure the rule, not the week.
      const latest = monthsOf(data).map((m) => m.summary.gateRule).filter(Boolean).pop();
      if (!latest) return null;
      const era = data.days.filter((d) => d.gate && d.gate.rule === latest);
      if (era.length < 30) return null;
      const bucket = (weekend) => era.filter((d) => (d.isoDow >= 6) === weekend)
        .reduce((a, d) => ({
          scored: a.scored + d.gate.scored,
          passed: a.passed + d.gate.qualified,
          drive: a.drive + d.driveHours,
          pax: a.pax + d.paxHours,
          ran: a.ran + d.active.total,
        }), { scored: 0, passed: 0, drive: 0, pax: 0, ran: 0 });
      const wd = bucket(false), we = bucket(true);
      if (!wd.scored || !we.scored) return null;
      const pw = (wd.passed / wd.scored) * 100, pe = (we.passed / we.scored) * 100;
      if (pw - pe < 5) return null;
      return {
        severity: 'structural',
        headline: `The day gate is a weekday gate: ${pw.toFixed(1)}% of weekday cab-days clear it `
                + `against ${pe.toFixed(1)}% at weekends`,
        body: `${n(wd.passed)} of ${n(wd.scored)} against ${n(we.passed)} of ${n(we.scored)}, under one rule `
            + `(${latest}). The cause is the shape of the threshold, not the demand: a running cab averages `
            + `${(wd.pax / wd.ran).toFixed(2)} passenger hours on a weekday and ${(we.pax / we.ran).toFixed(2)} `
            + `at the weekend against an absolute 4-hour bar, and a shorter shift cuts the tail above a fixed `
            + `line by far more than it cuts the mean.`,
        strength: `${n(wd.scored + we.scored)} scored cab-days inside the current gate era only — `
                + `July is excluded because it was scored under a different rule`,
        notThis: 'Not weekend demand being weaker. The same cabs run at weekends and are paid for what they '
               + 'deliver; what changes is how many of them reach a threshold designed around a weekday shift.',
      };
    },
  },

  // ── WHAT IS WORKING ───────────────────────────────────────────────────────
  {
    id: 'silenceIsTheLever',
    lens: 'good',
    run: (data, view) => {
      const cost = view.cabs.reduce((a, c) => a + c.silenceCost, 0);
      const s = view.month.summary;
      if (!cost || !s?.rupees) return null;
      const silent = view.cabs.reduce((a, c) => a + c.silentDays, 0);
      return {
        severity: 'good',
        headline: `The screens are already in the ground: ${inr(cost)} of ${view.month.label} `
                + `was left on the table by cabs that did not run`,
        body: `${plural(silent, 'fitted cab-day', 'fitted cab-days')} earned nothing, priced at the `
            + `month's median cab-day of ${inr(view.medianRate)}. Against ${inr(s.rupees)} actually `
            + `earned, closing that silence alone is a ${((s.rupees + cost) / s.rupees).toFixed(1)}× `
            + `month with no new hardware, no new city and no new advertiser.`,
        strength: `${plural(view.cabs.length, 'cab', 'cabs')} × ${plural(view.month.days.length, 'day', 'days')}`,
        notThis: 'Not a forecast and not lost revenue. There was no sale behind those cab-days; '
               + 'this is a ceiling computed at a rate the fleet has genuinely achieved.',
      };
    },
  },
  {
    id: 'whichLever',
    lens: 'good',
    run: (data, view) => {
      const p = view.month.summary?.potential;
      if (!p?.levers) return null;
      const u = p.levers.utilisationOnly, y = p.levers.yieldOnly;
      if (!u || !y) return null;
      const rate = (k) => p.rungs?.find((r) => r.key === k)?.rate;
      const bigger = u > y ? 'utilisation' : 'yield';
      const actual = view.month.summary.rupees;
      return {
        severity: 'good',
        headline: `${bigger === 'utilisation' ? 'Utilisation' : 'Yield'} is the bigger lever this month`,
        body: `UTILISATION — every fitted cab-day earning the median ${inr(rate('p50'))}, including the `
            + `ones that stayed dark: ${inr(u)} (${(u / actual).toFixed(1)}×). `
            + `YIELD — only the days that already ran, lifted to the 75th percentile ${inr(rate('p75'))}: `
            + `${inr(y)} (${(y / actual).toFixed(1)}×). Both at once: ${inr(p.levers.both)} `
            + `(${(p.levers.both / actual).toFixed(1)}×).`,
        strength: `${n(p.fittedCabDays)} fitted cab-days, ${n(p.earningCabDays)} of which earned `
                + `(${p.utilisationPct}%); rates are this fleet's own percentiles, not assumptions`,
        notThis: 'Not additive with the other observations, and the two levers are not independent — '
               + 'the combined figure is not the sum of the separate ones.',
      };
    },
  },
  {
    id: 'steadyCabs',
    lens: 'good',
    run: (data, view) => {
      const key = view.month.key;
      const steady = view.cabs.filter((c) => c.silentDays === 0 && (c.months?.[key] || 0) > 0);
      // One cab is an anecdote. A claim this page makes in a room has to rest
      // on at least a pair, or the first question kills it.
      if (steady.length < 2) return null;
      const per = steady.map((c) => c.months[key]);
      const rest = view.cabs.filter((c) => c.silentDays > 0 && (c.months?.[key] || 0) > 0)
        .map((c) => c.months[key]);
      if (!rest.length) return null;
      const a = median(per), b = median(rest);
      return {
        severity: 'good',
        headline: `${plural(steady.length, 'cab ran', 'cabs ran')} every single day of ${view.month.label}`,
        body: `They earned a median ${inr(a)} against ${inr(b)} for the cabs that went silent at least `
            + `once — ${(a / b).toFixed(1)}× on the same hardware, the same rate card and the same cities. `
            + `Best of them: ${steady.map((c) => c.plate).slice(0, 3).join(', ')}.`,
        strength: `${steady.length} uninterrupted against ${rest.length} interrupted cabs, one month`,
        notThis: 'Not a claim that uptime causes the difference — a cab that drives more both runs '
               + 'more and earns more. It does establish the number is reachable by this fleet.',
      };
    },
  },
  {
    id: 'lagMoney',
    lens: 'good',
    run: (data, view) => {
      if (!view.lagRupees) return null;
      const days = view.days.filter((d) => d.day.slice(0, 7) === view.month.key && d.lag);
      return {
        severity: 'structural',
        headline: `${inr(view.lagRupees)} of real delivery sits outside this page's totals`,
        body: `On ${plural(days.length, 'day', 'days')} a screen delivered before device_vehicles `
            + `recorded it as fitted. The page counts a screen only inside its binding window, so that `
            + `money is named here rather than silently absorbed into a total that would then disagree `
            + `with the registry.`,
        strength: `every delivery in the record tested against its own binding timestamp`,
        notThis: 'Not missing money and not double counting. It is money the registry had not yet '
               + 'been told about, and it reconciles once the binding is backdated.',
      };
    },
  },
  {
    // True by construction, not by coincidence: in server/routes/atlas.js the
    // `presence` CTE that produces months[].rupees has no join to
    // device_day_eligibility at all — only the separate `gate_money` CTE does.
    id: 'gateNotInRevenuePath',
    lens: 'gate',
    run: (data, view) => {
      const s = view.month.summary;
      if (!s?.gateScored) return null;
      return {
        severity: 'structural',
        headline: 'The gate decides who is paid, never what was earned',
        body: `${view.month.label}: ${inr(s.rupees)} of presence revenue, of which ${inr(s.gateRupees)} `
            + `(${pctOf(s.gateRupees, s.rupees)}%) sits on cab-days that met the gate. The two are computed `
            + `down separate paths — the revenue figure never joins the eligibility verdicts — so a pass rate `
            + `that moves cannot move the top line by a rupee.`,
        strength: `structural, verified against the query rather than the data: the presence CTE has no `
                + `join to device_day_eligibility`,
        notThis: 'Not that the gate is cosmetic. It decides the shareable half exactly, and therefore what '
               + 'the premise and device owners are paid — it simply is not a revenue control.',
      };
    },
  },
  {
    id: 'everyRupeeJudged',
    lens: 'good',
    run: (data, view) => {
      const total = data.days.reduce((a, d) => a + d.rupees, 0);
      const open = data.days.reduce((a, d) => a + d.unscoredRupees, 0);
      if (!total) return null;
      const openDays = data.days.filter((d) => d.unscoredRupees > 0).map((d) => d.day);
      // The claim is only worth making while the open money is today's.
      const onlyToday = openDays.length > 0 && openDays.every((d) => d === data.today?.day);
      if (!onlyToday && open / total > 0.01) return null;
      return {
        severity: 'good',
        headline: `No rupee on this page is more than a day old and still unjudged`,
        body: `${inr(total)} of presence revenue across ${n(data.days.length)} days, of which ${inr(open)} `
            + `is awaiting a verdict — ${openDays.length ? `all of it ${openDays.join(', ')}, still open` : 'none'}. `
            + `The sweep runs at 09:00 IST and scores the previous day; every earlier day already carries a `
            + `frozen verdict that no later recomputation rewrites.`,
        strength: `every day in the record checked for its own verdict; punctuality itself is a smaller `
                + `sample than that and should be read as a hint, not a guarantee`,
        notThis: 'Not a claim that the verdicts are CORRECT. It is a claim that they exist, are dated, and '
               + 'are frozen — which is what makes them auditable, not what makes them right.',
      };
    },
  },
];

export const LENSES = {
  decline: { label: 'What is falling', note: 'movement against the month before' },
  outlier: { label: 'What is uneven', note: 'spread across cabs and cities' },
  gate: { label: 'What the gate is doing', note: 'the rule, its verdicts, and its gaps' },
  good: { label: 'What is working', note: 'headroom this fleet has already demonstrated' },
};

export const SEVERITY = {
  act: { label: 'act', rank: 0 },
  watch: { label: 'watch', rank: 1 },
  structural: { label: 'how the instrument works', rank: 2 },
  good: { label: 'headroom', rank: 3 },
};

// Runs every rule over the current payload and returns only what fired. A rule
// that throws is dropped rather than taking the page down with it — an
// observation is commentary, and commentary must never cost the reader the
// figures it was commenting on.
export function observe(data, view) {
  const out = [];
  for (const r of RULES) {
    try {
      const o = r.run(data, view);
      if (o) out.push({ id: r.id, lens: r.lens, ...o });
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn(`observation ${r.id} failed`, e);
    }
  }
  return out.sort((a, b) => SEVERITY[a.severity].rank - SEVERITY[b.severity].rank);
}

export const RULE_COUNT = RULES.length;
